import React, { useState, useCallback } from 'react';
import { Spin, Modal, Input, Button, Tabs, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useJ219Mode } from './hooks/useJ219Mode';
import { useJ219Record } from './hooks/useJ219Record';
import { useModulePermission } from '../../hooks/usePermission';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import Page1Tab from './components/Page1Tab';
import Page2Tab from './components/Page2Tab';
import Page3Tab from './components/Page3Tab';
import WashLabelModal from './components/WashLabelModal';
import { BatchAcceptanceBridge, pwj219f1AcceptanceManifest } from '../../features/acceptance';
import { j219Api } from '../../services/j219Api';
import './J219Form.css';

const J219Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, enterAdd, enterEdit, enterBrowse } = useJ219Mode();
  const permissions = useModulePermission('pwj219f1');
  const {
    records, record, currentIdx, formData, details, loading,
    getField, setField, setDetails,
    navFirst, navPrev, navNext, navLast,
    handleAdd, handleEdit, handleSave, handleCancel, handleDelete,
    quickSearch,
  } = useJ219Record(mode, enterAdd, enterEdit, enterBrowse);

  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [washLabelVisible, setWashLabelVisible] = useState(false);

  const isApproved = (getField('fj740_80') as string) === 'P';
  const approvalState = {
    statusCode: String(getField('fj740_80') || ''),
    isApproved,
    isPending: getField('fj740_80') === 'S' || getField('fj740_80') === 'D',
    isLocked: isApproved,
  };

  const doSearch = useCallback(() => {
    quickSearch(searchText);
  }, [quickSearch, searchText]);

  const doDelete = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除分析單「${record.fj740_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: handleDelete,
    });
  }, [record, handleDelete]);

  const doExit = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePrint = useCallback(() => {
    message.info('列印功能尚未實作');
  }, []);

  const dateVal = getField('fj740_12') as string;
  const dateDisplay = dateVal ? dayjs(dateVal).format('YYYY-MM-DD') : '';

  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <Page1Tab
          formData={formData}
          isEditing={isEditing}
          isApproved={isApproved}
          getField={getField}
          setField={setField}
          onOpenWashLabel={() => setWashLabelVisible(true)}
          detailCount={details.length}
        />
      ),
    },
    {
      key: '2',
      label: '單層針織',
      children: (
        <Page2Tab
          formData={formData}
          isEditing={isEditing}
          isApproved={isApproved}
          getField={getField}
          setField={setField}
        />
      ),
    },
    {
      key: '3',
      label: '成品/物化性檢測',
      children: (
        <Page3Tab
          formData={formData}
          isEditing={isEditing}
          isApproved={isApproved}
          getField={getField}
          setField={setField}
        />
      ),
    },
  ];

  return (
    <div className="j219-form">
      <div className="j219-header">
        <h2 className="j219-title">J219-新產品開發分析-單層針織</h2>
        <div className="j219-search-bar">
          <Input
            placeholder="搜尋分析單號 / 產品代號"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={doSearch}
            style={{ width: 260 }}
            disabled={isEditing}
          />
          <Button onClick={doSearch} disabled={isEditing} style={{ marginLeft: 8 }}>
            搜尋
          </Button>
        </div>
      </div>

      {/* Form-level fields — always visible above tabs (FoxPro form-level) */}
      <div className="j219-form-level">
        <div className="j219-fl-field">
          <label>分析單號</label>
          <Input value={String(getField('fj740_01') || '')} disabled size="small" data-contract-field="fj740_01" />
        </div>
        <div className="j219-fl-field">
          <label>入單日期</label>
          <Input value={dateDisplay} disabled size="small" data-contract-field="fj740_12" />
        </div>
        {isEditing && mode === 'add' && <span className="j219-mode-badge j219-mode-add">新增模式</span>}
        {isEditing && mode === 'edit' && <span className="j219-mode-badge j219-mode-edit">修改模式</span>}
        {records.length > 0 && !isEditing && (
          <span className="j219-record-counter">{currentIdx + 1} / {records.length} 筆</span>
        )}
      </div>

      <Spin spinning={loading}>
        {!isEditing && !record && records.length === 0 ? (
          <div className="j219-empty">
            <p>尚無資料，請點「新增」或輸入關鍵字查詢</p>
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="j219-tabs"
          />
        )}
      </Spin>

      <WashLabelModal
        visible={washLabelVisible}
        isEditing={isEditing && !isApproved}
        details={details}
        onClose={() => setWashLabelVisible(false)}
        onUpdateDetails={setDetails}
      />

      <SharedActionBar
        mode={mode === 'add' ? 'add' : mode === 'edit' ? 'edit' : 'browse'}
        isEditing={isEditing}
        hasRecord={!!record}
        currentIdx={currentIdx}
        recordCount={records.length}
        onNavFirst={navFirst}
        onNavPrev={navPrev}
        onNavNext={navNext}
        onNavLast={navLast}
        onAdd={handleAdd}
        onSave={handleSave}
        onModify={handleEdit}
        onCancel={handleCancel}
        onDelete={doDelete}
        onPrint={handlePrint}
        onExit={doExit}
        approval={approvalState}
        canView={permissions.canView}
        canCreate={permissions.canCreate}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        canPrint={permissions.canView}
        acceptanceActionIds={{
          create: 'PWJ219-ACT-CREATE',
          delete: 'PWJ219-ACT-DELETE',
        }}
      />
      <BatchAcceptanceBridge
        manifest={pwj219f1AcceptanceManifest}
        records={records}
        selectedKeys={record?.fj740_01 ? [record.fj740_01] : []}
        keyOf={(item) => item.fj740_01}
        queryExact={(key) => j219Api.getById(key)}
        lastApiResponse={record}
        loading={loading}
        onAutoFill={navFirst}
        autoFillDisabled={records.length === 0}
        screenDraft={{ master: formData, details }}
      />
    </div>
  );
};

export default J219Form;
